import appLogger from '@/utils/logger';
import { DraggablePlayActionType, IDraggablePlayItem, IFlowNode } from '@/types/plays';
import { Edge, OnEdgesChange, useEdgesState, useNodesState } from '@xyflow/react';
import {
  createContext,
  Dispatch,
  FC,
  MutableRefObject,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

interface IDnDDragContextProps {
  type: DraggablePlayActionType | null;
  setType: any;
  item: any;
  setItem: any;
}

interface IDnDGraphContextProps {
  nodes: IFlowNode[];
  setNodes: Dispatch<SetStateAction<IFlowNode[]>>;
  edges: Edge[];
  setEdges: Dispatch<SetStateAction<Edge[]>>;
  onEdgesChange: OnEdgesChange<Edge>;
  handleDisconnectNodeRef: MutableRefObject<((node: IFlowNode) => void) | null>;
}

interface IDnDUIContextProps {
  selectedNode?: IDraggablePlayItem;
  setSelectedNode: Dispatch<SetStateAction<IDraggablePlayItem | undefined>>;
  isShowCreatePlayPanel: boolean;
  setIsShowCreatePlayPanel: Dispatch<SetStateAction<boolean>>;
  allPlays?: IDraggablePlayItem[];
  setAllPlays: Dispatch<SetStateAction<IDraggablePlayItem[]>>;
  isEditMode?: boolean;
  setIsEditMode: Dispatch<SetStateAction<boolean>>;
  workflowId?: number;
  setWorkflowId: Dispatch<SetStateAction<number | undefined>>;
  getActualConnectionId: () => number | undefined;
  audienceCreationMethod?: string;
  setAudienceCreationMethod: Dispatch<SetStateAction<string | undefined>>;
  /** The campaign's LinkedIn sender is chosen per signal (template/system/signal workflow). */
  linkedInSenderExempt?: boolean;
  setLinkedInSenderExempt: Dispatch<SetStateAction<boolean>>;
  // Refs for stable handler references (used by nodeTypes/edgeTypes)
  audienceNameRef: MutableRefObject<string>;
  audienceStartTimeRef: MutableRefObject<string | undefined>;
  onSelectTriggerNodeRef: MutableRefObject<(() => void) | undefined>;
}

// OPTIMIZATION: Separate context for autoSaved to prevent cascading re-renders
// When autoSaved changes, only components that read it (DragNodesPanel) re-render
// Components that only call setAutoSaved don't re-render
interface IDnDAutoSaveContextProps {
  autoSaved: boolean;
  setAutoSaved: Dispatch<SetStateAction<boolean>>;
}

interface IDnDProviderProps {
  children: ReactNode;
}

const DnDDragContext = createContext<IDnDDragContextProps>({} as IDnDDragContextProps);
const DnDGraphContext = createContext<IDnDGraphContextProps>({} as IDnDGraphContextProps);
const DnDUIContext = createContext<IDnDUIContextProps>({} as IDnDUIContextProps);
const DnDAutoSaveContext = createContext<IDnDAutoSaveContextProps>({} as IDnDAutoSaveContextProps);

export const DnDProvider: FC<IDnDProviderProps> = ({ children }) => {
  const [type, setType] = useState<DraggablePlayActionType | null>(null);
  const [item, setItem] = useState<any>();
  const [selectedNode, setSelectedNode] = useState<IDraggablePlayItem>();
  const [isShowCreatePlayPanel, setIsShowCreatePlayPanel] = useState(false);
  const [allPlays, setAllPlays] = useState<IDraggablePlayItem[]>([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const [workflowId, setWorkflowId] = useState<number | undefined>();
  const [nodes, setNodes] = useNodesState<IFlowNode>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);
  const [autoSaved, setAutoSavedInternal] = useState(false);
  const [audienceCreationMethod, setAudienceCreationMethod] = useState<string | undefined>();
  const [linkedInSenderExempt, setLinkedInSenderExempt] = useState(false);

  // Refs for stable handler references - these are updated by DragDropPlay
  // and read by nodeTypes/edgeTypes components without causing re-renders
  const handleDisconnectNodeRef = useRef<((node: IFlowNode) => void) | null>(null);
  const audienceNameRef = useRef<string>('');
  const audienceStartTimeRef = useRef<string | undefined>(undefined);
  const onSelectTriggerNodeRef = useRef<(() => void) | undefined>(undefined);

  // Wrap setSelectedNode to ensure proper state change detection and preserve connectionId
  const handleSetSelectedNode = (node: SetStateAction<IDraggablePlayItem | undefined>) => {
    console.log('[DnDContext] handleSetSelectedNode CALLED', {
      nodeType: typeof node === 'function' ? 'function' : (node as any)?.action_type,
      nodeConnectionId: typeof node === 'function' ? 'function' : (node as any)?.connectionId,
    });
    setSelectedNode((prev) => {
      // If passing a function, call it with the previous state
      if (typeof node === 'function') {
        return node(prev);
      }

      // If null/undefined, just return it
      if (!node) return node;

      // CRITICAL FIX: Force unique state object for React change detection
      const uniqueNode = {
        ...node,
        // Add unique timestamp to force React to detect state changes
        selectedAt: Date.now(),
        // Preserve existing selection timestamp if it exists
        ...((prev as any)?.selectedAt && { previousSelectedAt: (prev as any).selectedAt }),
      } as any;

      // Preserve a Google Search connectionId only for updates to the same
      // client node. Carrying it across node selections makes an unsaved node
      // PATCH the previously selected persisted node.
      const previousNodeId = prev?.reactFlowNodeId || prev?.nodeId || prev?.instanceId;
      const nextNodeId = node.reactFlowNodeId || node.nodeId || node.instanceId;
      if (
        node.action_type === 'serp_search'
        && prev?.connectionId
        && !node.connectionId
        && previousNodeId
        && previousNodeId === nextNodeId
      ) {
        appLogger.info('Preserving connectionId for Google Search node', { data: prev.connectionId });
        uniqueNode.connectionId = prev.connectionId;
      }

      appLogger.info('🎯 DnD Context: Setting selected node with unique timestamp:', {
                action_type: uniqueNode.action_type,
                connectionId: uniqueNode.connectionId,
                selectedAt: uniqueNode.selectedAt,
                reactFlowNodeId: uniqueNode.reactFlowNodeId,
              });

      return uniqueNode;
    });
  };

  // CRITICAL FIX: Compute connectionId once and store in a ref to prevent infinite re-renders
  // The issue was that getActualConnectionId() returned different values when nodes changed,
  // causing nodeConnectionId to change in child components, which triggered field path changes,
  // which triggered setNodes calls, which changed nodes again - creating an infinite loop.
  const lastConnectionIdRef = useRef<number | undefined>(undefined);
  const lastSelectedNodeIdRef = useRef<string | undefined>(undefined);

  // Use refs for values that shouldn't trigger function recreation
  // Updated via useEffect to avoid linter warning about ref access during render
  const nodesRef = useRef(nodes);
  const selectedNodeRef = useRef(selectedNode);

  useEffect(() => {
    nodesRef.current = nodes;
  }, [nodes]);

  useEffect(() => {
    selectedNodeRef.current = selectedNode;
  }, [selectedNode]);

  // Helper function to get the actual connectionId from flow nodes
  // CRITICAL: This function has EMPTY dependencies to prevent re-renders when nodes changes
  // It reads from refs instead, so it always has access to current values without changing its reference
  const getActualConnectionId = useCallback((): number | undefined => {
    const currentSelectedNode = selectedNodeRef.current;
    const currentNodes = nodesRef.current;

    if (!currentSelectedNode) {
      lastConnectionIdRef.current = undefined;
      lastSelectedNodeIdRef.current = undefined;
      return undefined;
    }

    // Check if we're still on the same selected node
    const currentNodeId = (currentSelectedNode as any).reactFlowNodeId || currentSelectedNode.nodeId || (currentSelectedNode as any).instanceId;

    // If same node, return cached value to prevent cascading updates
    if (currentNodeId && currentNodeId === lastSelectedNodeIdRef.current && lastConnectionIdRef.current !== undefined) {
      return lastConnectionIdRef.current;
    }

    // Compute new connectionId
    let connectionId: number | undefined = undefined;

    // CRITICAL: Prioritize selectedNode.connectionId to avoid nodes lookup
    if (currentSelectedNode.connectionId) {
      connectionId = currentSelectedNode.connectionId;
    } else if ((currentSelectedNode as any).reactFlowNodeId) {
      const flowNode = currentNodes.find((n) => n.id === (currentSelectedNode as any).reactFlowNodeId);
      if (flowNode?.data?.connectionId) connectionId = flowNode.data.connectionId;
    } else {
      // Use type assertion to access instanceId for unpersisted nodes
      const instanceId = (currentSelectedNode as any)?.instanceId;
      if (instanceId) {
        const flowNode = currentNodes.find((n) => (n.data as any)?.instanceId === instanceId);
        if (flowNode?.data?.connectionId) connectionId = flowNode.data.connectionId;
      }
    }

    // Cache the result
    lastConnectionIdRef.current = connectionId;
    lastSelectedNodeIdRef.current = currentNodeId;

    return connectionId;
  }, []); // CRITICAL: Empty dependencies - function reference never changes

  const dragValue = useMemo(
    () => ({
      type,
      setType,
      item,
      setItem,
    }),
    [type, item, setType, setItem],
  );

  const graphValue = useMemo(
    () => ({
      nodes,
      setNodes,
      edges,
      setEdges,
      onEdgesChange,
      handleDisconnectNodeRef,
    }),
    [nodes, edges, onEdgesChange, setNodes, setEdges],
  );

  const uiValue = useMemo(
    () => ({
      selectedNode,
      setSelectedNode: handleSetSelectedNode,
      isShowCreatePlayPanel,
      setIsShowCreatePlayPanel,
      allPlays,
      setAllPlays,
      isEditMode,
      setIsEditMode,
      workflowId,
      setWorkflowId,
      getActualConnectionId,
      audienceCreationMethod,
      setAudienceCreationMethod,
      linkedInSenderExempt,
      setLinkedInSenderExempt,
      audienceNameRef,
      audienceStartTimeRef,
      onSelectTriggerNodeRef,
    }),
    [
      selectedNode,
      isShowCreatePlayPanel,
      allPlays,
      isEditMode,
      workflowId,
      getActualConnectionId,
      audienceCreationMethod,
      linkedInSenderExempt,
      setIsShowCreatePlayPanel,
      setAllPlays,
      setIsEditMode,
      setWorkflowId,
      setAudienceCreationMethod,
      setLinkedInSenderExempt,
    ],
  );

  // OPTIMIZATION: Separate memoized value for autoSaved context
  // setAutoSavedInternal is stable (from useState), so this only changes when autoSaved changes
  const autoSaveValue = useMemo(
    () => ({
      autoSaved,
      setAutoSaved: setAutoSavedInternal,
    }),
    [autoSaved],
  );

  return (
    <DnDDragContext.Provider value={dragValue}>
      <DnDGraphContext.Provider value={graphValue}>
        <DnDUIContext.Provider value={uiValue}>
          <DnDAutoSaveContext.Provider value={autoSaveValue}>
            {children}
          </DnDAutoSaveContext.Provider>
        </DnDUIContext.Provider>
      </DnDGraphContext.Provider>
    </DnDDragContext.Provider>
  );
};

export const useDnDDrag = () => useContext(DnDDragContext);
export const useDnDGraph = () => useContext(DnDGraphContext);
export const useDnDUI = () => useContext(DnDUIContext);
export const useDnDAutoSave = () => useContext(DnDAutoSaveContext);
